// src/services/api/sellers.ts - Versión actualizada

import { Order } from '@/services/types/domain';
import { httpClient } from '../http/client';
import { ApiResponse } from '@/services/types/api';

export interface SellerRegistrationData {
    store_name?: string;
    store_description?: string;
    business_license?: string;
    tax_id?: string;
    business_address?: string;
    // Campos adicionales del formulario
    business_type?: 'individual' | 'company';
    contact_phone?: string;
    experience_years?: number;
}

export interface Seller {
    id: number;
    user_id: number;
    store_name: string;
    store_description?: string;
    store_logo?: string;
    store_banner?: string;
    business_license?: string;
    tax_id?: string;
    business_address?: string;
    verification_status: 'pending' | 'verified' | 'rejected';
    rating_average: number;
    total_reviews: number;
    total_sales: number;
    is_featured: boolean;
    commission_rate: number;
    created_at: string;
    updated_at: string;
}

export interface SellerDashboardData {
    stats: {
        total_products: number;
        total_orders: number;
        total_sales: number;
        pending_orders: number;
    };
    recent_orders: Array<{
        id: number;
        order_number: string;
        customer_name: string;
        total: number;
        status: string;
        created_at: string;
    }>;
    top_products: Array<{
        id: number;
        name: string;
        image: string;
        total_sales: number;
        revenue: number;
    }>;
    sales_chart?: Array<{
        date: string;
        sales: number;
    }>;
}
export interface OrderItem {
    product_id: number;
    product_name: string;
    quantity: number;
    price: number;
    image: string;
}

export interface SellerOrder {
    id: number;
    order_number: string;
    customer_name: string;
    total: number;
    status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'refunded';
    created_at: string;
    items: OrderItem[];
}

export interface OrdersResponse {
    data: SellerOrder[];
    pagination: {
        current_page: number;
        total_pages: number;
        total_items: number;
        per_page: number;
    };
}
export interface Product {
    id: number;
    name: string;
    slug: string;
    price: number;
    compare_price: number | null;
    rating_average: number;
    rating_count: number;
    primary_image: string;
    category_name: string;
    seller_name: string;
    in_stock: boolean;
    discount_percentage: number;
}

export interface ProductsResponse {
    success: boolean;
    data: Product[];
    pagination: {
        current_page: number;
        per_page: number;
        total: number;
        total_pages: number;
        has_more: boolean;
    };
}
export interface SellerOrderRow extends Order {
    seller_revenue_in_order: number;
    seller_items_in_order: number;
}

export interface SellerOrdersResult {
    items: SellerOrderRow[];
    meta: {
        current_page: number;
        per_page: number;
        total: number;
        last_page: number;
    };
}
export interface SellerOrderItem {
    id: number;
    order_id?: number;
    product_id: number;
    variant_id?: number | null;
    seller_id?: number;
    product_name: string;
    product_slug?: string;
    quantity: number;
    price: number | string; // viene como string del backend a veces
    total: number | string; // idem
    sku?: string;
    image_url?: string | null;
    store_name?: string;
}

export interface SellerTrackingEvent {
    status: string; // e.g. created, processing, shipped, delivered, cancelled
    note?: string | null;
    created_at: string; // fecha ISO o "YYYY-MM-DD HH:mm:ss"
}

export type SellerOrderDetail = Order & {
    items: SellerOrderItem[];
    seller_revenue_in_order?: number; // ingreso de esta tienda en la orden
    seller_items_in_order?: number;
    tracking?: SellerTrackingEvent[];
};
class SellersApi {
    private readonly basePath = '/seller';

    /**
     * Registrarse como vendedor - Con campos reales de la BD
     */
    async registerAsSeller(data: SellerRegistrationData): Promise<Seller> {
        const payload = {
            store_name: data.store_name,
            store_description: data.store_description || null,
            business_license: data.business_license || null,
            tax_id: data.tax_id,
            business_address: data.business_address,
            // Campos adicionales que el backend puede procesar
            business_type: data.business_type,
            contact_phone: data.contact_phone,
            experience_years: data.experience_years
        };

        const response = await httpClient.post<ApiResponse<Seller>>(
            `${this.basePath}/register`,
            payload
        );

        return response.data.data;
    }

    /**
     * Obtener perfil de vendedor
     */
    async getSellerProfile(): Promise<Seller> {
        const response = await httpClient.get<ApiResponse<Seller>>(
            `${this.basePath}/profile`
        );
        return response.data.data;
    }

    /**
     * Actualizar perfil de vendedor
     */
    async updateSellerProfile(data: {
        store_name: string;
        store_description?: string;
        business_license?: string;
        tax_id?: string;
        business_address?: string;
    }): Promise<Seller> {
        const response = await httpClient.put<ApiResponse<Seller>>(
            `${this.basePath}/profile`,
            data
        );
        return response.data.data;
    }
    async getSellerStatus(): Promise<{
        verification_status: 'none' | 'pending' | 'verified' | 'rejected';
        seller_info?: {
            id: number;
            store_name: string;
            verification_status: string;
            created_at: string;
        };
        user_type?: string;
        message: string;
    }> {
        const response = await httpClient.get<ApiResponse<any>>(
            `${this.basePath}/status`
        );
        return response.data.data;
    }
    /**
     * Obtener dashboard del vendedor
     */
    async getSellerDashboard(): Promise<SellerDashboardData> {
        const response = await httpClient.get<ApiResponse<SellerDashboardData>>(
            `${this.basePath}/dashboard`
        );
        return response.data.data;
    }

    /**
     * Subir logo de la tienda
     */
    async uploadStoreLogo(logo: File): Promise<{ logo_url: string }> {
        const formData = new FormData();
        formData.append('store_logo', logo);

        const response = await httpClient.upload<ApiResponse<{ logo_url: string }>>(
            `${this.basePath}/logo`,
            formData
        );

        return response.data.data;
    }

    /**
     * Subir banner de la tienda
     */
    async uploadStoreBanner(banner: File): Promise<{ banner_url: string }> {
        const formData = new FormData();
        formData.append('store_banner', banner);

        const response = await httpClient.upload<ApiResponse<{ banner_url: string }>>(
            `${this.basePath}/banner`,
            formData
        );

        return response.data.data;
    }

    /**
     * Obtener información pública de un vendedor
     */
    async getPublicSellerInfo(sellerId: number): Promise<{
        id: number;
        store_name: string;
        store_description?: string;
        store_logo?: string;
        rating_average?: number;
        rating_count: number;
        total_sales: number;
        verification_status: string;
        created_at: string;
    }> {
        const response = await httpClient.get<ApiResponse<any>>(
            `${this.basePath}/${sellerId}/public`
        );
        return response.data.data;
    }

    /**
     * Obtener productos de un vendedor específico (vista pública)
     */
    async getSellerProducts(
        sellerId: number,
        filters: { page?: number; per_page?: number; sort?: string; status?: string } = {}
    ): Promise<ProductsResponse> {
        const params = new URLSearchParams();
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined) {
                params.append(key, value.toString());
            }
        });

        const queryString = params.toString();
        const url = `${sellerId}/products${queryString ? `?${queryString}` : ''}`;
        //const url = `products${queryString ? `?${queryString}` : ''}`;

        const response = await httpClient.get<ProductsResponse>(url);
        return response.data; // ← devolvemos el objeto completo: { success, data, pagination }
    }

    /**
     * Verificar estado de verificación
     */
    async getVerificationStatus(): Promise<{
        status: 'pending' | 'verified' | 'rejected';
        message?: string;
        required_documents?: string[];
    }> {
        const response = await httpClient.get<ApiResponse<any>>(
            `${this.basePath}/verification-status`
        );
        return response.data.data;
    }

    async getSellerOrders2(filters: {
        page?: number;
        per_page?: number;
        status?: 'pending' | 'shipped' | 'delivered' | 'cancelled' | 'all';
        search?: string;
        sort_by?: 'created_at' | 'total' | 'customer_name' | 'order_number';
        sort_dir?: 'asc' | 'desc';
    } = {}): Promise<OrdersResponse> {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== 'all') {
                params.append(key, value.toString());
            }
        });

        const queryString = params.toString();
        const url = `${this.basePath}/orders${queryString ? `?${queryString}` : ''}`;

        const response = await httpClient.get<ApiResponse<OrdersResponse>>(url);
        return response.data.data;
    }
    async getSellerOrders(filters: {
        page?: number;
        per_page?: number;
        q?: string;            // mapeamos a ?search=
        status?: string;
        payment_status?: string;
        sort?: 'created_desc' | 'created_asc' | 'total_desc' | 'total_asc';
        date_from?: string;
        date_to?: string;
    } = {}): Promise<SellerOrdersResult> {
        const params: any = {
            page: filters.page ?? 1,
            per_page: filters.per_page ?? 20,
            search: filters.q ?? "",                 // <--- mapeo aquí
            status: filters.status || undefined,
            payment_status: filters.payment_status || undefined,
            sort: filters.sort || "created_desc",
            date_from: filters.date_from || undefined,
            date_to: filters.date_to || undefined,
        };

        const res = await httpClient.get<ApiResponse<any>>(this.basePath + "/orders", { params });
        const payload = res.data.data;         // backend data[]
        const pagination = (res.data as any).pagination;

        const items: SellerOrderRow[] = (payload ?? []).map((o: any) => ({
            // campos base del Order (usa tus nombres del domain.ts)
            id: toNum(o.id),
            order_number: o.order_number,
            user_id: toNum(o.user_id),
            status: o.status,
            payment_status: o.payment_status,
            currency: o.currency || "PEN",
            subtotal: String(o.subtotal ?? "0"),
            tax_amount: String(o.tax_amount ?? "0"),
            shipping_amount: String(o.shipping_amount ?? "0"),
            discount_amount: String(o.discount_amount ?? "0"),
            total_amount: String(o.total_amount ?? "0"),
            coupon_id: o.coupon_id ?? null,
            billing_address: o.billing_address,
            shipping_address: o.shipping_address,
            notes: o.notes ?? null,
            shipped_at: o.shipped_at ?? null,
            delivered_at: o.delivered_at ?? null,
            created_at: o.created_at,
            updated_at: o.updated_at,
            first_name: o.first_name,
            last_name: o.last_name,
            email: o.email,

            // extendidos del seller
            seller_revenue_in_order: toNum(o.seller_revenue_in_order, 0),
            seller_items_in_order: toNum(o.seller_items_in_order, 0),
        }));

        const meta = {
            current_page: pagination?.current_page ?? 1,
            per_page: pagination?.per_page ?? (items.length || 20),
            total: pagination?.total ?? items.length,
            last_page: pagination?.total_pages ?? 1,
        };

        return { items, meta };
    }

    /**
     * Actualizar el estado de una orden
     */
    async updateOrderStatus(orderId: number, status: 'shipped' | 'delivered' | 'cancelled', notes?: string): Promise<{ success: boolean }> {
        const response = await httpClient.put<ApiResponse<{ success: boolean }>>(
            `${this.basePath}/orders/${orderId}/status`,
            { status, notes }
        );
        return response.data.data;
    }
    async getSellerOrderById(orderId: number): Promise<SellerOrderDetail | ApiResponse<SellerOrderDetail>> {
        const response = await httpClient.get<ApiResponse<SellerOrderDetail>>(
            `${this.basePath}/orders/${orderId}`
        );
        return response.data.data;
    }
}

function toNum(v: any, d = 0) {
    if (v === null || v === undefined || v === "") return d;
    const n = typeof v === "string" ? parseFloat(v) : Number(v);
    return Number.isFinite(n) ? n : d;
}
// Instancia única del servicio
export const sellersApi = new SellersApi();
export default sellersApi;